import React, { useState, useEffect, useCallback } from 'react';
import {
  getVerifications,
  getVerificationStats
} from '../Config/verificationService';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import { formatEasternDate } from '../Utils/dateTime';

function Verification() {
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    verification_type: '',
    search: '',
    page: 1,
    limit: 10,
    sort_by: '',
    sort_order: ''
  });
  const [documentsPendingOnly, setDocumentsPendingOnly] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const navigate = useNavigate();

  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getVerifications(filters);
      if (response.success) {
        setVerifications(response.verifications);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch verifications');
        toast.error('Failed to fetch verifications');
      }
    } catch (error) {
      setError('Failed to fetch verifications');
      toast.error('Failed to fetch verifications');
    }
    setLoading(false);
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await getVerificationStats();
      if (response.success) {
        setStats(response.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchVerifications();
  }, [fetchVerifications]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handleStatusCardClick = (status) => {
    setDocumentsPendingOnly(false);
    setFilters(prev => ({
      ...prev,
      status: prev.status === status ? '' : status,
      page: 1
    }));
  };

  const handleDocumentsPendingClick = () => {
    setFilters(prev => ({
      ...prev,
      status: '',
      page: 1
    }));
    setDocumentsPendingOnly(prev => !prev);
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      verification_type: '',
      search: '',
      page: 1,
      limit: 10,
      sort_by: '',
      sort_order: ''
    });
    setDocumentsPendingOnly(false);
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUSPENDED':
        return 'bg-orange-100 text-orange-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationTypeColor = (type) => {
    switch (type) {
      case 'CONTRACTOR':
        return 'bg-blue-100 text-blue-800';
      case 'WORKER':
        return 'bg-purple-100 text-purple-800';
      case 'ADMIN':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewDetails = (verification) => {
    navigate(`/verifications/${verification.id}`);
  };

  const hasPendingDocuments = (verification) => {
    const docs = Array.isArray(verification?.documents) ? verification.documents : [];
    return docs.some((doc) => String(doc?.status || '').toUpperCase() === 'PENDING');
  };

  const filteredVerifications = documentsPendingOnly
    ? verifications.filter(hasPendingDocuments)
    : verifications;

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">User Verifications</h1>
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <button
              type="button"
              onClick={() => handleStatusCardClick('PENDING')}
              aria-pressed={filters.status === 'PENDING' && !documentsPendingOnly}
              className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${filters.status === 'PENDING' && !documentsPendingOnly ? 'ring-2 ring-[#EF5E28]' : ''
                }`}
            >
              <h3 className="text-sm font-medium text-gray-500">Pending</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {(stats.verifications.CONTRACTOR?.PENDING || 0) +
                  (stats.verifications.WORKER?.PENDING || 0) +
                  (stats.verifications.ADMIN?.PENDING || 0)}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleStatusCardClick('VERIFIED')}
              aria-pressed={filters.status === 'VERIFIED' && !documentsPendingOnly}
              className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${filters.status === 'VERIFIED' && !documentsPendingOnly ? 'ring-2 ring-[#EF5E28]' : ''
                }`}
            >
              <h3 className="text-sm font-medium text-gray-500">Verified</h3>
              <p className="text-2xl font-bold text-green-600">
                {(stats.verifications.CONTRACTOR?.VERIFIED || 0) +
                  (stats.verifications.WORKER?.VERIFIED || 0) +
                  (stats.verifications.ADMIN?.VERIFIED || 0)}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleStatusCardClick('REJECTED')}
              aria-pressed={filters.status === 'REJECTED' && !documentsPendingOnly}
              className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${filters.status === 'REJECTED' && !documentsPendingOnly ? 'ring-2 ring-[#EF5E28]' : ''
                }`}
            >
              <h3 className="text-sm font-medium text-gray-500">Rejected</h3>
              <p className="text-2xl font-bold text-red-600">
                {(stats.verifications.CONTRACTOR?.REJECTED || 0) +
                  (stats.verifications.WORKER?.REJECTED || 0) +
                  (stats.verifications.ADMIN?.REJECTED || 0)}
              </p>
            </button>
            <button
              type="button"
              onClick={() => handleStatusCardClick('SUSPENDED')}
              aria-pressed={filters.status === 'SUSPENDED' && !documentsPendingOnly}
              className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${filters.status === 'SUSPENDED' && !documentsPendingOnly ? 'ring-2 ring-[#EF5E28]' : ''
                }`}
            >
              <h3 className="text-sm font-medium text-gray-500">Suspended</h3>
              <p className="text-2xl font-bold text-orange-600">
                {(stats.verifications.CONTRACTOR?.SUSPENDED || 0) +
                  (stats.verifications.WORKER?.SUSPENDED || 0) +
                  (stats.verifications.ADMIN?.SUSPENDED || 0)}
              </p>
            </button>
            <button
              type="button"
              onClick={handleDocumentsPendingClick}
              aria-pressed={documentsPendingOnly}
              className={`bg-white p-4 rounded-lg shadow text-left transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#EF5E28] ${documentsPendingOnly ? 'ring-2 ring-[#EF5E28]' : ''
                }`}
            >
              <h3 className="text-sm font-medium text-gray-500">Documents Pending</h3>
              <p className="text-2xl font-bold text-blue-600">
                {stats.documents?.PENDING || 0}
              </p>
            </button>
          </div>
        )}

        {/* Filters Section */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
                <option value="SUSPENDED">Suspended</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="verification_type"
                value={filters.verification_type}
                onChange={handleFilterChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">All Types</option>
                <option value="CONTRACTOR">Contractor</option>
                <option value="WORKER">Worker</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by name, email, mobile..."
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleClearFilters}
                className="text-white px-4 py-2 rounded hover:opacity-90"
                style={{ backgroundColor: '#EF5E28' }}
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Verifications Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden text-left">
          {loading ? (
            <div className="p-8 text-center">Loading...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-600">{error}</div>
          ) : filteredVerifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No verifications found</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documents
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredVerifications.map((verification) => (
                      <tr key={verification.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {verification.user.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {verification.user.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              {verification.user.mobile}
                            </div>
                            {verification.user.business_name && (
                              <div className="text-sm text-gray-500">
                                {verification.user.business_name}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVerificationTypeColor(verification.verification_type)}`}>
                            {verification.verification_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(verification.status)}`}>
                            {verification.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {verification.documents?.length || 0} documents
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatEasternDate(verification.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleViewDetails(verification)}
                            className="font-semibold hover:opacity-80 transition-opacity"
                            style={{ color: '#002F53' }}
                          >
                            Update Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={pagination.page <= 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(pagination.page * pagination.limit, pagination.total)}
                      </span>{' '}
                      of <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      {[...Array(pagination.pages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => handlePageChange(i + 1)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${pagination.page === i + 1
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
}

export default Verification; 
